import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, FolderOpen, UploadSimple, WarningCircle, CheckCircle,
  CircleNotch, ArrowsLeftRight,
} from "@phosphor-icons/react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * PSA folder → listing photo matcher.
 *
 * Folder layout (as described by PSA users):
 *   root/
 *     56789ABCDEF/
 *       front.jpg
 *       back.jpg
 *     11223XYZ/
 *       from.jpg   (PSA typo/mislabel occasionally)
 *       back.jpg
 *
 * We use <input webkitdirectory> to read the whole tree client-side. Each
 * subfolder's name must START with the cert number of the target listing.
 * The two image files inside are identified by their filename prefix:
 *   "front" or "from" → front image
 *   "back"            → back image
 * A per-row "Swap" checkbox handles the case where PSA labeled them wrong.
 */
export default function PsaPhotos() {
  const { user } = useAuth();
  const fileRef = useRef();
  const [myListings, setMyListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(true);
  const [rows, setRows] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const isVendor = user?.is_vendor || user?.role === "admin";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/listings/mine");
        setMyListings((data.listings || []).filter((l) => l.status !== "removed"));
      } catch (err) { toast.error(formatErr(err)); }
      finally { setLoadingListings(false); }
    })();
  }, []);

  const listingsWithCert = myListings.filter((l) => l.cert_number);

  const onFolderPicked = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Group files by their immediate subfolder (parts[1] because parts[0] is
    // the root folder the user selected).
    const grouped = new Map();
    for (const f of files) {
      const rel = f.webkitRelativePath || f.name;
      const parts = rel.split("/");
      if (parts.length < 2) continue;               // ignore loose files at root
      const sub = parts[1];
      if (!/\.(jpe?g|png|webp|heic|heif)$/i.test(f.name)) continue; // images only
      if (!grouped.has(sub)) grouped.set(sub, []);
      grouped.get(sub).push(f);
    }

    // Build rows: extract cert prefix, front/back file, matched listing.
    const certLookup = new Map();
    for (const l of listingsWithCert) {
      certLookup.set(String(l.cert_number).trim(), l);
    }

    const nextRows = [];
    for (const [sub, files] of grouped.entries()) {
      const certMatch = sub.match(/^(\d+)/);
      const cert = certMatch ? certMatch[1] : null;
      const listing = cert ? certLookup.get(cert) : null;

      let front = files.find((f) => /^front/i.test(f.name)) || files.find((f) => /^from/i.test(f.name));
      let back = files.find((f) => /^back/i.test(f.name));
      // Fallback: if only two files and we couldn't identify by prefix, guess by order.
      if (!front && !back && files.length === 2) {
        front = files[0];
        back = files[1];
      }

      nextRows.push({
        subfolder: sub,
        cert,
        listing,
        front,
        back,
        frontUrl: front ? URL.createObjectURL(front) : null,
        backUrl: back ? URL.createObjectURL(back) : null,
        swap: false,
        status: null,       // 'ok' | 'err' | null
        error: null,
      });
    }
    // Sort: matched rows first, then unmatched (so the user can act on wins fast)
    nextRows.sort((a, b) => {
      if (!!a.listing !== !!b.listing) return a.listing ? -1 : 1;
      return a.subfolder.localeCompare(b.subfolder);
    });
    setRows(nextRows);
    setResult(null);
  };

  const toggleSwap = (idx) => {
    setRows((r) => r.map((row, i) => (i === idx ? { ...row, swap: !row.swap } : row)));
  };

  const runUpload = async () => {
    const eligible = rows.filter((r) => r.listing && r.front && r.back);
    if (!eligible.length) {
      toast.error("No rows are ready to upload — need a matched listing + front + back per row.");
      return;
    }
    setProcessing(true);
    const results = { updated: 0, errors: [] };

    // Process sequentially to keep memory + progress predictable.
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!(row.listing && row.front && row.back)) continue;
      setRows((r) => r.map((x, idx) => (idx === i ? { ...x, status: "uploading" } : x)));
      try {
        // Choose which local file goes where based on the per-row swap toggle.
        const frontFile = row.swap ? row.back : row.front;
        const backFile = row.swap ? row.front : row.back;

        const uploadOne = async (file) => {
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await api.post("/uploads/image", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          return data.id;
        };

        const [frontId, backId] = await Promise.all([uploadOne(frontFile), uploadOne(backFile)]);
        await api.patch(`/listings/${row.listing.id}`, { front_image: frontId, back_image: backId });
        results.updated++;
        setRows((r) => r.map((x, idx) => (idx === i ? { ...x, status: "ok" } : x)));
      } catch (err) {
        const msg = formatErr(err);
        results.errors.push({ subfolder: row.subfolder, message: msg });
        setRows((r) => r.map((x, idx) => (idx === i ? { ...x, status: "err", error: msg } : x)));
      }
    }
    setProcessing(false);
    setResult(results);
    if (results.errors.length === 0) {
      toast.success(`Updated ${results.updated} listing${results.updated === 1 ? "" : "s"}`);
    } else {
      toast(`${results.updated} updated · ${results.errors.length} error${results.errors.length === 1 ? "" : "s"}`, { icon: "⚠" });
    }
  };

  const reset = () => {
    // Free the object-URLs we created for previews to avoid a memory leak.
    rows.forEach((r) => {
      if (r.frontUrl) URL.revokeObjectURL(r.frontUrl);
      if (r.backUrl) URL.revokeObjectURL(r.backUrl);
    });
    setRows([]);
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  if (!isVendor) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="psa-vendor-gate">
        <div className="font-mono text-xs text-[#FFD700] tracking-widest uppercase">→ Vendor Account Required</div>
        <h1 className="mt-3 font-display font-black uppercase text-3xl">PSA Photo Upload</h1>
        <p className="mt-4 text-zinc-400">Only vendor accounts can add photos to listings.</p>
        <Link to="/account?upgrade=1" className="h57-btn-primary inline-block mt-6">Upgrade to Vendor →</Link>
      </div>
    );
  }

  const matchedCount = rows.filter((r) => r.listing && r.front && r.back).length;
  const unmatchedCount = rows.length - matchedCount;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 md:py-8">
      <Link to="/sell/bulk" data-testid="psa-back" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white mb-3">
        <ArrowLeft size={12} weight="bold" /> Back to Bulk
      </Link>
      <div className="font-mono text-xs text-[#FF5F1F] tracking-widest uppercase">→ Bulk Photo Upload</div>
      <h1 className="mt-2 font-display font-black uppercase text-3xl md:text-4xl tracking-tighter">PSA Folder Match</h1>
      <p className="mt-4 text-zinc-400 leading-relaxed max-w-2xl">
        Pick the folder that contains one sub-folder per PSA slab. Each sub-folder
        should be named starting with the cert number. Front and back JPGs inside are
        matched by filename prefix (<span className="text-zinc-200">front</span>, <span className="text-zinc-200">from</span>, or <span className="text-zinc-200">back</span>).
        If PSA mixed the labels, tick <span className="text-zinc-200">Swap</span> on that row.
      </p>

      {/* Step 1 — pool overview */}
      <section className="mt-8 h57-surface p-4" data-testid="psa-pool">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">→ Your listings with a cert number</div>
            <div className="mt-1 font-display font-bold text-lg">
              {loadingListings ? "Loading…" : `${listingsWithCert.length} listing${listingsWithCert.length === 1 ? "" : "s"} ready to receive photos`}
            </div>
          </div>
          <div className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">
            {loadingListings ? "" : `${myListings.length - listingsWithCert.length} listing${myListings.length - listingsWithCert.length === 1 ? "" : "s"} skipped (no cert)`}
          </div>
        </div>
      </section>

      {/* Step 2 — folder picker */}
      <section className="mt-6 h57-surface p-6" data-testid="psa-step-pick">
        <div className="flex items-start gap-4">
          <div className="font-display font-black text-3xl text-[#FF5F1F] tracking-tighter leading-none">01</div>
          <div className="flex-1">
            <h2 className="font-display font-black uppercase text-lg tracking-tight">Pick the parent folder</h2>
            <p className="mt-2 text-sm text-zinc-400">
              Every sub-folder should be named like <span className="font-mono text-zinc-300">56789ABCDEF/</span> — leading digits = cert number.
              Two image files inside: one starting with <span className="font-mono text-zinc-300">front</span>/<span className="font-mono text-zinc-300">from</span>, one with <span className="font-mono text-zinc-300">back</span>.
            </p>
            <label
              htmlFor="psa-folder"
              className={`mt-4 flex flex-col items-center justify-center border-2 border-dashed transition-colors cursor-pointer py-10 px-4 text-center ${
                rows.length ? "border-[#FF5F1F] bg-[#FF5F1F]/5" : "border-white/15 hover:border-white/30"
              }`}
              data-testid="psa-folder-drop"
            >
              <FolderOpen size={32} weight="bold" className={rows.length ? "text-[#FF5F1F]" : "text-zinc-500"} />
              <div className="mt-2 font-mono text-xs uppercase tracking-widest">
                {rows.length ? `${rows.length} folder${rows.length === 1 ? "" : "s"} loaded` : "Click to choose the parent folder"}
              </div>
              <div className="mt-1 font-mono text-[10px] text-zinc-500">Chrome / Edge / Safari · nothing is uploaded yet</div>
              <input
                id="psa-folder"
                ref={fileRef}
                type="file"
                webkitdirectory=""
                directory=""
                multiple
                accept="image/*"
                data-testid="psa-folder-input"
                className="hidden"
                onChange={onFolderPicked}
              />
            </label>
          </div>
        </div>
      </section>

      {/* Step 3 — preview & confirm */}
      {rows.length > 0 && (
        <section className="mt-6 h57-surface p-6" data-testid="psa-step-preview">
          <div className="flex items-start gap-4">
            <div className="font-display font-black text-3xl text-[#FF5F1F] tracking-tighter leading-none">02</div>
            <div className="flex-1">
              <h2 className="font-display font-black uppercase text-lg tracking-tight">Review matches</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="h57-chip h57-chip-orange" data-testid="psa-matched-count">
                  <CheckCircle size={12} weight="fill" /> {matchedCount} ready
                </span>
                {unmatchedCount > 0 && (
                  <span className="h57-chip !border-red-500/50 !text-red-400" data-testid="psa-unmatched-count">
                    <WarningCircle size={12} weight="fill" /> {unmatchedCount} unmatched
                  </span>
                )}
              </div>

              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-zinc-500 uppercase tracking-widest border-b border-white/10">
                    <tr>
                      <th className="text-left p-2">Folder / Cert</th>
                      <th className="text-left p-2">Matched Listing</th>
                      <th className="text-center p-2">Front</th>
                      <th className="text-center p-2">Back</th>
                      <th className="text-center p-2">Swap</th>
                      <th className="text-center p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const ready = row.listing && row.front && row.back;
                      const displayedFront = row.swap ? row.backUrl : row.frontUrl;
                      const displayedBack = row.swap ? row.frontUrl : row.backUrl;
                      return (
                        <tr key={row.subfolder} className="border-b border-white/5 align-middle" data-testid={`psa-row-${row.subfolder}`}>
                          <td className="p-2">
                            <div className="font-mono text-xs">{row.cert || <span className="text-red-400">no cert</span>}</div>
                            <div className="font-mono text-[10px] text-zinc-500 truncate max-w-[160px]">{row.subfolder}</div>
                          </td>
                          <td className="p-2 text-xs">
                            {row.listing ? (
                              <div>
                                <div className="text-zinc-200 truncate max-w-[220px]">{row.listing.title}</div>
                                <div className="font-mono text-[10px] text-zinc-500">${row.listing.price}</div>
                              </div>
                            ) : (
                              <span className="text-red-400 font-mono text-xs uppercase tracking-widest">no listing</span>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            {displayedFront ? (
                              <img src={displayedFront} alt="front" className="w-12 h-16 object-contain bg-[#0A0A0A] border border-white/10 mx-auto" />
                            ) : <span className="text-red-400 font-mono text-[10px]">—</span>}
                          </td>
                          <td className="p-2 text-center">
                            {displayedBack ? (
                              <img src={displayedBack} alt="back" className="w-12 h-16 object-contain bg-[#0A0A0A] border border-white/10 mx-auto" />
                            ) : <span className="text-red-400 font-mono text-[10px]">—</span>}
                          </td>
                          <td className="p-2 text-center">
                            <label className="inline-flex items-center gap-1 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={row.swap}
                                onChange={() => toggleSwap(i)}
                                data-testid={`psa-swap-${row.subfolder}`}
                                disabled={processing || !ready}
                              />
                              <ArrowsLeftRight size={12} weight="bold" className={row.swap ? "text-[#FF5F1F]" : "text-zinc-500"} />
                            </label>
                          </td>
                          <td className="p-2 text-center">
                            {row.status === "uploading" ? (
                              <CircleNotch size={16} weight="bold" className="text-[#FF5F1F] animate-spin mx-auto" />
                            ) : row.status === "ok" ? (
                              <CheckCircle size={16} weight="fill" className="text-emerald-400 mx-auto" data-testid={`psa-row-ok-${row.subfolder}`} />
                            ) : row.status === "err" ? (
                              <span title={row.error} data-testid={`psa-row-err-${row.subfolder}`}>
                                <WarningCircle size={16} weight="fill" className="text-red-400 mx-auto" />
                              </span>
                            ) : ready ? (
                              <span className="font-mono text-[10px] text-zinc-500 uppercase">ready</span>
                            ) : (
                              <span className="font-mono text-[10px] text-red-400 uppercase" title={!row.listing ? "No matching listing found" : "Missing front or back image"}>skip</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={runUpload}
                  disabled={processing || matchedCount === 0}
                  data-testid="psa-upload-all"
                  className="h57-btn-primary inline-flex items-center gap-2"
                >
                  <UploadSimple size={16} weight="bold" />
                  {processing ? "Uploading…" : `Upload & attach ${matchedCount} row${matchedCount === 1 ? "" : "s"}`}
                </button>
                <button
                  type="button"
                  onClick={reset}
                  disabled={processing}
                  data-testid="psa-reset"
                  className="h57-btn-secondary text-sm"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Step 4 — result summary */}
      {result && (
        <section className="mt-6 h57-surface p-6" data-testid="psa-result">
          <div className="flex items-start gap-4">
            <div className="font-display font-black text-3xl text-[#FF5F1F] tracking-tighter leading-none">03</div>
            <div className="flex-1">
              <h2 className="font-display font-black uppercase text-lg tracking-tight">Result</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="h57-chip h57-chip-orange"><CheckCircle size={12} weight="fill" /> {result.updated} updated</span>
                {result.errors.length > 0 && (
                  <span className="h57-chip !border-red-500/50 !text-red-400"><WarningCircle size={12} weight="fill" /> {result.errors.length} errors</span>
                )}
              </div>
              {result.errors.length > 0 && (
                <ul className="mt-3 text-xs space-y-1">
                  {result.errors.map((e, i) => (
                    <li key={i} className="border-b border-white/5 pb-1 flex gap-2">
                      <span className="font-mono text-red-400 shrink-0">{e.subfolder}</span>
                      <span className="text-zinc-400">{e.message}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4">
                <Link to="/my-listings" className="h57-btn-primary text-sm">Go to My Listings →</Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
